import { Autocomplete, AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import { getProjects } from "containers/Projects/Projects.selectors";
import { ImportSourceLocation } from "containers/Resources/components/ImportSessionModal/useImportSession";
import { useMemo } from "react";
import { useSelector } from "react-redux";

/** Remap config sent to the backend: record type -> source name -> target name. */
export type ImportRemap = Record<string, Record<string, string>>;

/** Key of one source location in the UI state. */
export const sourceLocationKey = (location: { record_type: string; name: string }) =>
    `${location.record_type}::${location.name}`;

/**
 * Build the backend `remap` config from the per-location choices. Only a
 * location mapped to a DIFFERENT name produces an entry, so an untouched table
 * sends no remap at all (the import behaves exactly as before). Returns
 * undefined when nothing is remapped.
 */
export const buildRemapConfig = (
    locations: ImportSourceLocation[],
    choices: Record<string, string>
): ImportRemap | undefined => {
    const remap: ImportRemap = {};
    locations.forEach((location) => {
        const target = choices[sourceLocationKey(location)];
        if (!target || target === location.name) {
            return;
        }
        remap[location.record_type] = {
            ...(remap[location.record_type] ?? {}),
            [location.name]: target,
        };
    });
    return Object.keys(remap).length > 0 ? remap : undefined;
};

/**
 * Picker options for one source location: every existing location of the same
 * type (by name, since the remap is name-based), plus the source name itself
 * when it does not exist on the target, so the default "keep as is" choice is
 * always selectable and labelled as missing.
 */
export const locationRemapOptions = (
    location: ImportSourceLocation,
    existingNames: string[]
): AutocompleteOption[] => {
    const sorted = [...existingNames].sort((a, b) => a.localeCompare(b));
    const options: AutocompleteOption[] = sorted.map((name) => ({
        value: name,
        label: name === location.name ? `${name} (same name)` : name,
    }));
    if (!existingNames.includes(location.name)) {
        options.unshift({
            value: location.name,
            label: `${location.name} (does not exist on this workspace)`,
        });
    }
    return options;
};

interface ImportLocationRemapProps {
    locations: ImportSourceLocation[];
    /** source-location key -> chosen target name */
    choices: Record<string, string>;
    disabled?: boolean;
    onChange: (location: ImportSourceLocation, targetName: string) => void;
}

/**
 * "Import into" table: one row per project/bundle the export refers to, each
 * with a picker for the project/bundle on this workspace the records should go
 * to. Defaults to the location of the same name.
 */
export const ImportLocationRemap = ({
    locations,
    choices,
    disabled = false,
    onChange,
}: ImportLocationRemapProps) => {
    const projects = useSelector(getProjects);
    const bundles = useSelector(getBundles);
    const namesByType = useMemo(
        () => ({
            project: (projects ?? []).map((project: { name: string }) => project.name),
            bundle: (bundles ?? []).map((bundle: { name: string }) => bundle.name),
        }),
        [projects, bundles]
    );

    if (locations.length === 0) {
        return null;
    }

    return (
        <div className="import-location-remap">
            <div className="import-location-remap__title">Import into</div>
            <table className="import-location-remap__table">
                <thead>
                    <tr>
                        <th>From the export</th>
                        <th>Into this workspace</th>
                    </tr>
                </thead>
                <tbody>
                    {locations.map((location) => {
                        const key = sourceLocationKey(location);
                        const existing =
                            namesByType[location.record_type as "project" | "bundle"] ?? [];
                        return (
                            <tr key={key}>
                                <td className="import-location-remap__source">
                                    <span className="import-location-remap__type">
                                        {location.record_type}
                                    </span>{" "}
                                    {location.name}
                                </td>
                                <td>
                                    <Autocomplete
                                        name={`remap-${key}`}
                                        options={{
                                            list: locationRemapOptions(location, existing),
                                        }}
                                        value={choices[key] ?? location.name}
                                        isDisabled={disabled}
                                        onChange={(target) =>
                                            onChange(location, target ?? location.name)
                                        }
                                        additionalProps={{
                                            clearable: false,
                                            fieldWidth: 360,
                                            customKey: `remap-${key}`,
                                            menuPortal: true,
                                            menuPlacement: "auto",
                                        }}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ImportLocationRemap;
